// Pure compute layer for PM POSHAN reports.
// Inputs: schoolInfo (object), vendorInfo (object), months (array of MonthData).
// Outputs: monthlySummaries, totals, annexureEntries, odiaReceiptGroups, ingredientWiseReport, warnings.

import {
  CLASS_CATEGORIES,
  FUND_COMPONENTS,
  GRAIN_ITEMS,
  ODIA_RECEIPT_GROUPS,
  QUARTERS,
  classifyReceiptIngredient,
  displayIngredientName,
  ingredientAmount,
  vendorForGroup,
  toNumber,
} from "./poshanConstants";

export function getFundClosing(months, monthIndex, key) {
  if (monthIndex < 0 || !months[monthIndex]) return 0;
  const opening = getFundOpening(months, monthIndex, key);
  const entry = months[monthIndex].funds[key];
  return opening + entry.received - entry.expenditure;
}

export function getFundOpening(months, monthIndex, key) {
  if (monthIndex <= 0) return months[0]?.funds[key]?.opening || 0;
  return getFundClosing(months, monthIndex - 1, key);
}

export function getGrainClosing(months, monthIndex, key) {
  if (monthIndex < 0 || !months[monthIndex]) return 0;
  const opening = getGrainOpening(months, monthIndex, key);
  const entry = months[monthIndex].grains[key];
  return opening + entry.received - entry.consumption;
}

export function getGrainOpening(months, monthIndex, key) {
  if (monthIndex <= 0) return months[0]?.grains[key]?.opening || 0;
  return getGrainClosing(months, monthIndex - 1, key);
}

export function buildMonthlySummaries(months) {
  return months.map((month, monthIndex) => {
    const fundReceived = FUND_COMPONENTS.reduce((t, item) => t + month.funds[item.key].received, 0);
    const fundExpenditure = FUND_COMPONENTS.reduce((t, item) => t + month.funds[item.key].expenditure, 0);
    const ingredientTotal = month.ingredients.reduce((t, item) => t + ingredientAmount(item), 0);
    const cookTotal = month.cooks.reduce((t, cook) => t + cook.amount, 0);
    const riceConsumption = GRAIN_ITEMS.filter((g) => g.item === "Rice").reduce((t, g) => t + month.grains[g.key].consumption, 0);
    const wheatConsumption = GRAIN_ITEMS.filter((g) => g.item === "Wheat").reduce((t, g) => t + month.grains[g.key].consumption, 0);
    const endingFundBalance = FUND_COMPONENTS.reduce((t, item) => t + getFundClosing(months, monthIndex, item.key), 0);
    const endingRiceBalance = GRAIN_ITEMS.filter((g) => g.item === "Rice").reduce((t, g) => t + getGrainClosing(months, monthIndex, g.key), 0);
    const enrollment =
      month.meals.balVatikaEnrollment + month.meals.primaryEnrollment + month.meals.upperPrimaryEnrollment;
    return {
      monthName: month.monthName,
      fundReceived,
      fundExpenditure,
      ingredientTotal,
      cookTotal,
      riceConsumption,
      wheatConsumption,
      endingFundBalance,
      endingRiceBalance,
      mealsServed: month.meals.totalMealsServed,
      enrollment,
      physicalTarget: enrollment * month.meals.workingDays,
    };
  });
}

export function buildTotals(months, monthlySummaries) {
  const totalGrantReceived = monthlySummaries.reduce((t, m) => t + m.fundReceived, 0);
  const totalCookingCost = monthlySummaries.reduce((t, m) => t + m.fundExpenditure, 0);
  const totalIngredientExpenditure = monthlySummaries.reduce((t, m) => t + m.ingredientTotal, 0);
  const totalCookPayment = monthlySummaries.reduce((t, m) => t + m.cookTotal, 0);
  const totalRiceConsumption = monthlySummaries.reduce((t, m) => t + m.riceConsumption, 0);
  const totalWheatConsumption = monthlySummaries.reduce((t, m) => t + m.wheatConsumption, 0);
  const totalMealsServed = monthlySummaries.reduce((t, m) => t + m.mealsServed, 0);
  const totalExpenditure = totalCookingCost + totalCookPayment;
  const ucUtilizedAmount = totalCookingCost;
  const remainingBalance = FUND_COMPONENTS.reduce((t, item) => t + getFundClosing(months, months.length - 1, item.key), 0);
  const currentYearUcBalance = totalGrantReceived - ucUtilizedAmount;
  const studentsBenefited = Math.max(...monthlySummaries.map((m) => m.enrollment), 0);
  const physicalTarget = monthlySummaries.reduce((t, m) => t + m.physicalTarget, 0);
  const utilizationPercentage = totalGrantReceived ? (ucUtilizedAmount / totalGrantReceived) * 100 : 0;
  const achievementPercentage = physicalTarget ? (totalMealsServed / physicalTarget) * 100 : 0;
  return {
    totalGrantReceived, totalCookingCost, totalIngredientExpenditure, totalCookPayment,
    totalRiceConsumption, totalWheatConsumption, totalMealsServed, totalExpenditure,
    ucUtilizedAmount, remainingBalance, currentYearUcBalance, studentsBenefited,
    physicalTarget, utilizationPercentage, achievementPercentage,
  };
}

export function buildAnnexureEntries(months, schoolInfo, vendorInfo, cchsMaster = [], dryRationData = null) {
  const entries = [];
  let voucherSerial = 1;
  months.forEach((month) => {
    const grouped = new Map();
    month.ingredients.forEach((ingredient) => {
      const amount = ingredientAmount(ingredient);
      if (amount <= 0) return;
      const key = classifyReceiptIngredient(ingredient);
      grouped.set(key, (grouped.get(key) || 0) + amount);
    });
    ODIA_RECEIPT_GROUPS.forEach((group) => {
      const amount = grouped.get(group.key) || 0;
      if (amount <= 0) return;
      // Ladoo Making is a SERVICE paid to CCHs (not a vendor). Split per CCH if known so the
      // Annexure-1 reflects the same recipients shown on the hand-receipt. Falls back to
      // the CCH master roster when month.cooks is empty (typical for new generators).
      // Amount per cook = pieces × Rs 0.26 (matches the hand-receipt's qty × rate exactly).
      // Annexure-1 is an English document — show the English name of every CCH/vendor.
      if (group.key === "ladooMaking") {
        const LADOO_RATE = 0.26;
        const payable = month.cooks.filter((c) => (c.name || "").trim() || c.amount > 0);
        const masterCooks = (cchsMaster || []).map((c, i) => ({
          id: c.id || `cch-${i}`,
          name: c.name || c.nameOdia || `CCH ${i + 1}`,
        }));
        const cooks = payable.length
          ? payable
          : (masterCooks.length > 0 ? masterCooks : [{ id: "cch", name: "CCH" }]);
        const totalPieces = Math.round(amount / LADOO_RATE);
        const base = Math.floor(totalPieces / cooks.length);
        const remainder = totalPieces - base * cooks.length;
        cooks.forEach((cook, idx) => {
          const pieces = base + (idx === cooks.length - 1 ? remainder : 0);
          const cookAmount = Number((pieces * LADOO_RATE).toFixed(2));
          // English-first for the Annexure-1 details column.
          const cchName = cook.name || cook.nameOdia || `CCH ${idx + 1}`;
          entries.push({
            voucher: String(voucherSerial++).padStart(3, "0"),
            date: `${month.monthName} ${schoolInfo.financialYear}`,
            details: `Paid to ${cchName} for the purpose of Ladoo Making Charges for the Month of ${month.monthName}`,
            amount: cookAmount,
            remarks: `Ladoo Making Charges - ${month.monthName}`,
          });
        });
        return;
      }
      const vendor = vendorForGroup(vendorInfo, group.key);
      const vendorName = vendor.name || "Vendor";
      entries.push({
        voucher: String(voucherSerial++).padStart(3, "0"),
        date: `${month.monthName} ${schoolInfo.financialYear}`,
        details: `Paid to ${vendorName} for the purpose of ${group.english} Items for the Month of ${month.monthName}`,
        amount,
        remarks: `${group.english} - ${month.monthName}`,
      });
    });
  });
  // Append Dry-Ration cooking cost as a separate Annexure-1 line so the
  // grand total = (MDM days cooking cost) + (DR period cooking cost). DR
  // rice itself does NOT appear here — only the cash cooking cost paid
  // under the FSA (Food Security Allowance) provision is bookable against
  // PM-POSHAN funds. Quarter label used as the "date" so the auditor sees
  // it's not a month-specific voucher.
  const drCost = Number((dryRationData?.totals?.grandCost || 0).toFixed(2));
  const drDays = dryRationData?.totals?.days || 0;
  if (drCost > 0) {
    entries.push({
      voucher: String(voucherSerial++).padStart(3, "0"),
      date: `${QUARTERS[schoolInfo.quarter]?.label || "Quarter"} ${schoolInfo.financialYear}`,
      details: `Cooking Cost distributed under FSA (Food Security Allowance) for ${drDays} Dry-Ration day(s) during the quarter`,
      amount: drCost,
      remarks: `Dry Ration · FSA · ${drDays} day(s)`,
    });
  }
  return entries;
}

// Map a CCH master record (from /api/cchs) to the cook shape expected by the
// receipt builders. CCH master uses `fatherName` / `fatherNameOdia` while
// receipts read `father` / `fatherOdia` — normalise here.
//
// iter124 — propagate `entityType` + `agencyNameOdia` so the Hand-Receipt
// renderer can drop the father line and show the agency name instead.
function cookFromCchMaster(cch, fallbackId) {
  return {
    id: cch.id || fallbackId,
    entityType: cch.entityType || "individual",
    agencyNameOdia: cch.agencyNameOdia || "",
    name: cch.name || "",
    nameOdia: cch.nameOdia || "",
    father: cch.fatherName || "",
    fatherOdia: cch.fatherNameOdia || "",
    relation: cch.relationType === "husband" ? "husband" : "father",
    village: cch.village || "",
    villageOdia: cch.villageOdia || "",
    address: cch.address || "",
    addressOdia: cch.addressOdia || "",
    post: cch.post || "",
    postOdia: cch.postOdia || "",
    thana: cch.thana || "",
    thanaOdia: cch.thanaOdia || "",
    block: cch.block || "",
    blockOdia: cch.blockOdia || "",
    district: cch.district || "",
    districtOdia: cch.districtOdia || "",
    pincode: cch.pincode || "",
    pincodeOdia: cch.pincodeOdia || "",
    gender: cch.gender || "",
    category: cch.category || "",
    monthlyHonorarium: cch.monthlyHonorarium || 0,
    amount: 0,
    mode: cch.defaultMode || "Bank",
  };
}

// Enrich a `cook` (from month.cooks) with CCH master data by matching name.
// Empty cook fields fall back to the master's. If the cook itself has no name
// but a CCH master is provided as a positional fallback, use it.
function enrichCookFromMaster(cook, cchsMaster) {
  if (!Array.isArray(cchsMaster) || cchsMaster.length === 0) return cook;
  const trimmed = (cook?.name || "").trim();
  const trimmedOdia = (cook?.nameOdia || "").trim();
  const match = cchsMaster.find((c) => {
    const cn = (c.name || "").trim();
    const co = (c.nameOdia || "").trim();
    return (trimmed && (cn === trimmed)) || (trimmedOdia && (co === trimmedOdia));
  });
  if (!match) return cook;
  const m = cookFromCchMaster(match, cook.id);
  // Cook-level explicit values win; otherwise inherit from the master.
  return {
    ...m,
    ...Object.fromEntries(Object.entries(cook || {}).filter(([, v]) => v !== "" && v !== undefined && v !== null)),
  };
}

export function buildOdiaReceiptGroups(months, schoolInfo, vendorInfo, cchsMaster = [], vendorsMaster = []) {
  const quarterLabel = QUARTERS[schoolInfo.quarter].label;
  let voucherSerial = 1;
  const nextVoucher = () => String(voucherSerial++).padStart(3, "0");
  const receipts = [];

  // Enrich the vendor (from vendorInfo flat-map) with master vendor record by
  // name match — this lets address / father / pincode flow into receipts when
  // the user picked the vendor in Setup but didn't manually copy each field
  // into the Generator's vendor-info form.
  const enrichVendor = (vendor) => {
    if (!vendor || !Array.isArray(vendorsMaster) || vendorsMaster.length === 0) return vendor;
    const target = (vendor.name || "").trim();
    const targetOdia = (vendor.nameOdia || "").trim();
    if (!target && !targetOdia) return vendor;
    const m = vendorsMaster.find((vm) => {
      const vn = (vm.name || "").trim();
      const vo = (vm.nameOdia || "").trim();
      return (target && vn === target) || (targetOdia && vo === targetOdia);
    });
    if (!m) return vendor;
    // Map master → vendor shape (master uses fatherName / fatherNameOdia).
    const master = {
      name: m.name || "",
      nameOdia: m.nameOdia || "",
      entityType: m.entityType || "individual",
      agencyNameOdia: m.agencyNameOdia || "",
      father: m.fatherName || "",
      fatherOdia: m.fatherNameOdia || "",
      relation: m.relationType === "husband" ? "husband" : "father",
      village: m.village || "",
      villageOdia: m.villageOdia || "",
      address: m.address || "",
      addressOdia: m.addressOdia || "",
      post: m.post || "",
      postOdia: m.postOdia || "",
      thana: m.thana || "",
      thanaOdia: m.thanaOdia || "",
      block: m.block || "",
      blockOdia: m.blockOdia || "",
      district: m.district || "",
      districtOdia: m.districtOdia || "",
      pincode: m.pincode || "",
      pincodeOdia: m.pincodeOdia || "",
    };
    return {
      ...master,
      ...Object.fromEntries(Object.entries(vendor).filter(([, v]) => v !== "" && v !== undefined && v !== null)),
    };
  };

  const monthItems = (groupKey, month) =>
    month.ingredients
      .filter((ingredient) => classifyReceiptIngredient(ingredient) === groupKey && ingredientAmount(ingredient) > 0)
      .map((ingredient) => ({
        voucher: nextVoucher(),
        date: `${month.monthName} ${schoolInfo.financialYear}`,
        name: displayIngredientName(ingredient),
        quantity: ingredient.quantity,
        unit: ingredient.unit,
        rate: ingredient.rate,
        amount: ingredientAmount(ingredient),
      }));

  ODIA_RECEIPT_GROUPS.forEach((group) => {
    const vendor = enrichVendor(vendorForGroup(vendorInfo, group.key));

    if (group.key === "ladooMaking") {
      const LADOO_RATE = 0.26; // Rs per piece
      // Resolve the CCH list for this entire quarter:
      // 1. Use month.cooks where any cook has a non-empty name (enriched).
      // 2. Otherwise (the typical case where Generator hasn't been edited)
      //    use the full CCH master roster. This is what the user expects:
      //    "should generate only ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ ହାତ ରସିଦ in CCH Name and her informations".
      const namedFromMonths = new Map();
      months.forEach((month) => {
        (month.cooks || []).forEach((cook) => {
          if ((cook?.name || "").trim() || (cook?.nameOdia || "").trim()) {
            namedFromMonths.set(cook.id || cook.name, enrichCookFromMaster(cook, cchsMaster));
          }
        });
      });
      const masterCooks = (cchsMaster || []).map((c, i) => cookFromCchMaster(c, `cch-${i}`));
      const quarterCooks = namedFromMonths.size > 0
        ? Array.from(namedFromMonths.values())
        : masterCooks.length > 0 ? masterCooks : [{ id: "cch", name: "CCH" }];

      // Distribute integer pieces across cooks; the last cook absorbs the
      // rounding remainder so the total piece-count = round(totalLadoo / RATE)
      // and every receipt's amount = pieces × RATE (qty × rate is internally
      // consistent — was previously showing rounded pieces but raw amount,
      // so 261 × 0.26 = 67.86 was displayed as Rs 67.99).
      const splitPieces = (totalAmount, n) => {
        const totalPieces = Math.round(totalAmount / LADOO_RATE);
        const base = Math.floor(totalPieces / n);
        const remainder = totalPieces - base * n;
        return Array.from({ length: n }, (_, i) => base + (i === n - 1 ? remainder : 0));
      };

      months.forEach((month) => {
        const totalLadoo = month.ingredients
          .filter((ing) => classifyReceiptIngredient(ing) === "ladooMaking")
          .reduce((s, ing) => s + ingredientAmount(ing), 0);
        if (totalLadoo <= 0) return;
        const payable = (month.cooks || []).filter((c) => (c?.name || "").trim() || (c?.nameOdia || "").trim());
        const monthCooks = payable.length
          ? payable.map((c) => enrichCookFromMaster(c, cchsMaster))
          : (masterCooks.length > 0 ? masterCooks : [{ id: "cch", name: "CCH", amount: 0, mode: "Bank" }]);
        const piecesPerCook = splitPieces(totalLadoo, monthCooks.length);
        monthCooks.forEach((cook, index) => {
          const cchName = cook.name || `CCH ${index + 1}`;
          const cchNameOdia = cook.nameOdia || "";
          const itemDisplayName = cchNameOdia || cchName;
          const pieces = piecesPerCook[index];
          const amount = Number((pieces * LADOO_RATE).toFixed(2));
          receipts.push({
            ...group,
            key: `${group.key}-${month.monthName}-${cook.id || index}`,
            period: month.monthName,
            items: [{
              voucher: nextVoucher(),
              date: `${month.monthName} ${schoolInfo.financialYear}`,
              name: `ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ - ${itemDisplayName}`,
              quantity: pieces, unit: "pieces", rate: LADOO_RATE, amount,
            }],
            total: amount,
            vendorName: vendor.name, vendorNameOdia: vendor.nameOdia,
            vendorEntityType: vendor.entityType || "individual",
            vendorAgencyNameOdia: vendor.agencyNameOdia || "",
            vendorFather: vendor.father, vendorFatherOdia: vendor.fatherOdia,
            vendorVillage: vendor.village, vendorVillageOdia: vendor.villageOdia,
            vendorAddress: vendor.address, vendorAddressOdia: vendor.addressOdia,
            vendorPost: vendor.post || "", vendorPostOdia: vendor.postOdia || "",
            vendorThana: vendor.thana || "", vendorThanaOdia: vendor.thanaOdia || "",
            vendorBlock: vendor.block || "", vendorBlockOdia: vendor.blockOdia || "",
            vendorDistrict: vendor.district || "", vendorDistrictOdia: vendor.districtOdia || "",
            vendorPincode: vendor.pincode || "", vendorPincodeOdia: vendor.pincodeOdia || "",
            isQuarter: false, cchName,
            cchNameOdia: cook.nameOdia || "",
            cchEntityType: cook.entityType || "individual",
            cchAgencyNameOdia: cook.agencyNameOdia || "",
            cchFather: cook.father || "", cchFatherOdia: cook.fatherOdia || "",
            cchVillage: cook.village || "", cchVillageOdia: cook.villageOdia || "",
            cchAddress: cook.address || "", cchAddressOdia: cook.addressOdia || "",
            cchPost: cook.post || "", cchPostOdia: cook.postOdia || "",
            cchThana: cook.thana || "", cchThanaOdia: cook.thanaOdia || "",
            cchBlock: cook.block || "", cchBlockOdia: cook.blockOdia || "",
            cchDistrict: cook.district || "", cchDistrictOdia: cook.districtOdia || "",
            cchPincode: cook.pincode || "", cchPincodeOdia: cook.pincodeOdia || "",
          });
        });
      });

      const quarterTotalLadoo = months.reduce((sum, month) => {
        return sum + month.ingredients
          .filter((ing) => classifyReceiptIngredient(ing) === "ladooMaking")
          .reduce((s, ing) => s + ingredientAmount(ing), 0);
      }, 0);

      if (quarterTotalLadoo > 0) {
        const cchList = quarterCooks;
        const cchCount = cchList.length;
        // Per-month per-cook integer-pieces split (same algorithm as monthly
        // receipt) so qty × rate = amount on every line and the quarterly
        // totals reconcile to the actual totalLadoo.
        const monthLadooTotals = months.map((m) =>
          m.ingredients
            .filter((ing) => classifyReceiptIngredient(ing) === "ladooMaking")
            .reduce((sum, ing) => sum + ingredientAmount(ing), 0),
        );
        const monthSplits = monthLadooTotals.map((mt) => splitPieces(mt, cchCount));
        cchList.forEach((cook, index) => {
          const cchNameOdia = cook.nameOdia || "";
          const itemDisplayName = cchNameOdia || cook.name || `CCH ${index + 1}`;
          const items = months.map((month, mIdx) => {
            const monthPieces = monthSplits[mIdx][index] || 0;
            const monthAmount = Number((monthPieces * LADOO_RATE).toFixed(2));
            return {
              voucher: nextVoucher(),
              date: `${month.monthName} ${schoolInfo.financialYear}`,
              name: `${month.monthName} ଅଂଶ - ${itemDisplayName}`,
              quantity: monthPieces, unit: "pieces", rate: LADOO_RATE, amount: monthAmount,
            };
          });
          const total = items.reduce((s, i) => s + i.amount, 0);
          receipts.push({
            ...group,
            key: `${group.key}-quarter-${cook.id || index}`,
            period: `${quarterLabel} Quarterly`,
            items,
            total,
            vendorName: vendor.name, vendorNameOdia: vendor.nameOdia,
            vendorEntityType: vendor.entityType || "individual",
            vendorAgencyNameOdia: vendor.agencyNameOdia || "",
            vendorFather: vendor.father, vendorFatherOdia: vendor.fatherOdia,
            vendorVillage: vendor.village, vendorVillageOdia: vendor.villageOdia,
            vendorAddress: vendor.address, vendorAddressOdia: vendor.addressOdia,
            vendorPost: vendor.post || "", vendorPostOdia: vendor.postOdia || "",
            vendorThana: vendor.thana || "", vendorThanaOdia: vendor.thanaOdia || "",
            vendorBlock: vendor.block || "", vendorBlockOdia: vendor.blockOdia || "",
            vendorDistrict: vendor.district || "", vendorDistrictOdia: vendor.districtOdia || "",
            vendorPincode: vendor.pincode || "", vendorPincodeOdia: vendor.pincodeOdia || "",
            isQuarter: true,
            cchName: cook.name || `CCH ${index + 1}`,
            cchNameOdia: cook.nameOdia || "",
            cchEntityType: cook.entityType || "individual",
            cchAgencyNameOdia: cook.agencyNameOdia || "",
            cchFather: cook.father || "", cchFatherOdia: cook.fatherOdia || "",
            cchVillage: cook.village || "", cchVillageOdia: cook.villageOdia || "",
            cchAddress: cook.address || "", cchAddressOdia: cook.addressOdia || "",
            cchPost: cook.post || "", cchPostOdia: cook.postOdia || "",
            cchThana: cook.thana || "", cchThanaOdia: cook.thanaOdia || "",
            cchBlock: cook.block || "", cchBlockOdia: cook.blockOdia || "",
            cchDistrict: cook.district || "", cchDistrictOdia: cook.districtOdia || "",
            cchPincode: cook.pincode || "", cchPincodeOdia: cook.pincodeOdia || "",
          });
        });
      }
    } else {
      months.forEach((month) => {
        const items = monthItems(group.key, month);
        receipts.push({
          ...group,
          key: `${group.key}-${month.monthName}`,
          period: month.monthName,
          items,
          total: items.reduce((s, i) => s + i.amount, 0),
          vendorName: vendor.name, vendorNameOdia: vendor.nameOdia,
          vendorEntityType: vendor.entityType || "individual",
          vendorAgencyNameOdia: vendor.agencyNameOdia || "",
          vendorFather: vendor.father, vendorFatherOdia: vendor.fatherOdia,
          vendorVillage: vendor.village, vendorVillageOdia: vendor.villageOdia,
          vendorAddress: vendor.address, vendorAddressOdia: vendor.addressOdia,
          vendorPost: vendor.post, vendorPostOdia: vendor.postOdia,
          vendorThana: vendor.thana, vendorThanaOdia: vendor.thanaOdia,
          vendorBlock: vendor.block, vendorBlockOdia: vendor.blockOdia,
          vendorDistrict: vendor.district, vendorDistrictOdia: vendor.districtOdia,
          vendorPincode: vendor.pincode, vendorPincodeOdia: vendor.pincodeOdia,
          isQuarter: false,
        });
      });
      const quarterItems = months.map((month) => {
        const total = month.ingredients
          .filter((ing) => classifyReceiptIngredient(ing) === group.key)
          .reduce((s, ing) => s + ingredientAmount(ing), 0);
        return {
          voucher: nextVoucher(),
          date: `${month.monthName} ${schoolInfo.financialYear}`,
          name: `${group.english} monthly total / ${month.monthName} ମୋଟ`,
          quantity: 1, unit: "month", rate: total, amount: total,
        };
      });
      receipts.push({
        ...group,
        key: `${group.key}-quarter`,
        period: `${quarterLabel} Quarterly`,
        items: quarterItems,
        total: quarterItems.reduce((s, i) => s + i.amount, 0),
        vendorName: vendor.name, vendorNameOdia: vendor.nameOdia,
        vendorFather: vendor.father, vendorFatherOdia: vendor.fatherOdia,
        vendorVillage: vendor.village, vendorVillageOdia: vendor.villageOdia,
        vendorAddress: vendor.address, vendorAddressOdia: vendor.addressOdia,
        vendorPost: vendor.post, vendorPostOdia: vendor.postOdia,
        vendorThana: vendor.thana, vendorThanaOdia: vendor.thanaOdia,
        vendorBlock: vendor.block, vendorBlockOdia: vendor.blockOdia,
        vendorDistrict: vendor.district, vendorDistrictOdia: vendor.districtOdia,
        vendorPincode: vendor.pincode, vendorPincodeOdia: vendor.pincodeOdia,
        isQuarter: true,
      });
    }
  });
  return receipts;
}

export function buildIngredientWiseReport(months) {
  const map = new Map();
  months.forEach((month) => {
    month.ingredients.forEach((ingredient) => {
      const name = displayIngredientName(ingredient);
      const key = name.trim().toLowerCase();
      const existing = map.get(key) ?? { name, quantity: 0, amount: 0, unit: ingredient.unit };
      existing.quantity += toNumber(ingredient.quantity);
      existing.amount += ingredientAmount(ingredient);
      existing.unit = existing.unit || ingredient.unit;
      map.set(key, existing);
    });
  });
  return Array.from(map.values()).filter((i) => i.quantity > 0 || i.amount > 0);
}

export function buildWarnings(schoolInfo, months, totals) {
  const list = [];
  const required = [
    ["schoolName", "School Name"],
    ["udiseCode", "UDISE Code"],
    ["district", "District"],
    ["block", "Block"],
    ["financialYear", "Financial Year"],
  ];
  required.forEach(([key, label]) => {
    if (!String(schoolInfo[key] ?? "").trim()) list.push(`${label} is mandatory.`);
  });
  months.forEach((month, i) => {
    if (month.meals.totalMealsServed <= 0) list.push(`${month.monthName}: Total Meals Served is missing.`);
    FUND_COMPONENTS.forEach((fund) => {
      const closing = getFundClosing(months, i, fund.key);
      if (closing < 0) list.push(`${month.monthName}: ${fund.label} has a negative closing balance.`);
    });
    GRAIN_ITEMS.forEach((grain) => {
      const closing = getGrainClosing(months, i, grain.key);
      if (closing < 0) list.push(`${month.monthName}: ${grain.category} ${grain.item} has a negative closing balance.`);
    });
  });
  if (totals.totalGrantReceived > 0 && totals.ucUtilizedAmount - totals.totalGrantReceived > 1) {
    list.push("UC utilized amount is greater than current quarter grant received.");
  }
  // Ingredients are cooked-meal (MDM) purchases, so compare with the MDM part only.
  const drCost = months.reduce((t, m) => t + FUND_COMPONENTS.reduce((s, f) => s + (Number(m._drFundsByClass?.[f.key]) || 0), 0), 0);
  if (totals.totalIngredientExpenditure > 0 && Math.abs(totals.totalIngredientExpenditure - (totals.totalCookingCost - drCost)) > 1) {
    list.push("Ingredient total and cooking cost expenditure do not match. Please verify vouchers.");
  }
  return list;
}
